import { useParams, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CITY_CONTENT } from "../data/cities";
import { SERVICES } from "../data/services";
import { Seo, faqSchema, breadcrumbSchema } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TrustStrip } from "../components/TrustStrip";
import { FaqAccordion } from "../components/FaqAccordion";
import { EstimateForm } from "../components/EstimateForm";
import { CtaBlock } from "../components/CtaBlock";
import { Reveal, Eyebrow } from "../components/Reveal";
import { Img } from "../components/Img";
import { BIZ, cleanText } from "../data/site";
import NotFound from "./NotFound";

// Rotated per-city so no photo is repeated across every service-area page.
// All are 1000px+ wide real shop photos — never upscaled at full width.
const BACKDROPS = {
  carrollton: "westway-collision-shop-carrollton-tx",
  "farmers-branch": "corvette-repair-after-carrollton",
  addison: "performance-tuning-carrollton-tx",
  coppell: "westway-auto-repair-bay",
  dallas: "collision-repair-carrollton-tx",
  lewisville: "happy-customer-westway-collision",
  plano: "customer-chevrolet-tahoe-westway",
  irving: "westway-collision-shop-carrollton-tx",
};
const FEATURED = ["collision-repair", "frame-straightening", "hail-damage-repair", "paintless-dent-repair", "paint-booth", "adas-calibration"];

export default function CityPage() {
  const { slug } = useParams();
  const city = CITY_CONTENT.find((c) => c.slug === slug);
  if (!city) return <NotFound />;

  const path = `/service-areas/${city.slug}`;
  const schemas = [
    faqSchema(city.faqs),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Service Areas", path: "/service-areas/carrollton" },
      { name: `${city.name}, TX`, path },
    ]),
  ];
  const featured = FEATURED.map((f) => SERVICES.find((s) => s.slug === f)).filter(Boolean);

  return (
    <div data-testid={`city-page-${city.slug}`}>
      <Seo title={city.metaTitle} description={city.metaDesc} path={path} schemas={schemas} />

      <section className="relative pt-32 lg:pt-40 pb-20 overflow-hidden noise-overlay">
        <div className="absolute inset-0" aria-hidden="true">
          <Img name={BACKDROPS[city.slug] || BACKDROPS.carrollton} alt="" bleed eager className="h-full w-full object-cover opacity-25" sizes="100vw" aria-hidden="true" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/90 via-ink/85 to-ink" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Service Areas", path: "/service-areas/carrollton" }, { name: `${city.name}, TX`, path }]} />
          <Reveal>
            <Eyebrow>Service Area — DFW Metroplex</Eyebrow>
            <h1 className="mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.06] text-balance max-w-4xl">
              {city.h1}
            </h1>
            <p className="mt-6 max-w-3xl text-base sm:text-lg text-slate-300 leading-relaxed">{cleanText(city.intro)}</p>
            <p className="mt-4 max-w-3xl text-sm sm:text-base text-slate-400 leading-relaxed">{cleanText(city.local)}</p>
          </Reveal>
        </div>
      </section>

      <TrustStrip />

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-10">
            <Eyebrow>Available in {city.name}</Eyebrow>
            <h2 className="mt-4 font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">
              Certified Services for {city.name} Drivers
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((s) => (
              <Reveal key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`city-${city.slug}-${s.slug}`}
                  className="group flex h-full items-center justify-between gap-4 rounded-2xl border border-white/[0.08] bg-surface p-6 transition-all duration-500 hover:border-accent/30 hover:-translate-y-1"
                >
                  <div>
                    <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-accent/80">{s.badge}</span>
                    <h3 className="mt-1.5 font-display text-lg font-semibold text-slate-100 group-hover:text-accent transition-colors">{s.menuName}</h3>
                  </div>
                  <ArrowRight size={17} className="shrink-0 text-mute group-hover:text-accent group-hover:translate-x-1 transition-all" aria-hidden="true" />
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8">
            <Link to="/services" data-testid={`city-${city.slug}-all-services`} className="inline-flex items-center gap-2 min-h-11 font-mono text-[11px] tracking-[0.2em] uppercase text-accent hover:text-accentdeep transition-colors">
              View all 15 services <ArrowRight size={13} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-20 surface-panel border-y border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-10">
            <Eyebrow>Local Answers</Eyebrow>
            <h2 className="mt-4 font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">
              {city.name} Collision Repair FAQs
            </h2>
          </Reveal>
          <FaqAccordion faqs={city.faqs} idPrefix={`city-faq-${city.slug}`} />
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-2xl border border-white/[0.08] bg-surface p-7 sm:p-10">
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">
                Free Estimate for {city.name} Drivers
              </h2>
              <p className="mt-3 text-sm sm:text-base text-slate-400">
                Send photos and vehicle details — a master technician responds within one business
                hour. Prefer to talk? Call{" "}
                <a href={BIZ.phoneHref} className="text-accent hover:text-accentdeep transition-colors">{BIZ.phoneDisplay}</a>.
              </p>
              <div className="mt-8">
                <EstimateForm compact testid={`city-estimate-${city.slug}`} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CtaBlock heading={`Serving ${city.name} From Our Carrollton Facility`} sub={`${BIZ.addressLine} — ${BIZ.hoursShort}.`} />
        </div>
      </section>
    </div>
  );
}
